import { create } from 'zustand';
import { MiniAppId } from '../utils/miniAppsIcons';

// Types pour les filtres des mini-apps
export interface MiniAppFilters {
  products?: {
    search?: string;
    category?: string;
    tags?: Set<string>;
    mentionedIds?: string[];
  };
  faq?: {
    search?: string;
    category?: string;
  };
  // Ajouter d'autres mini-apps selon les besoins
}

interface MiniAppModalState {
  activeApp: MiniAppId | null;
  filters: MiniAppFilters;
  setActiveApp: (appId: MiniAppId | null, appFilters?: any) => void;
  updateFilters: (appId: MiniAppId, newFilters: any) => void;
  openModal: (appId: MiniAppId, appFilters?: any) => void;
}

// Pour rétrocompatibilité avec le composant MiniAppModal.tsx
interface MiniAppModalLegacyState {
  activeModal: MiniAppId | null;
  closeModal: () => void;
  setActiveApp?: (appId: MiniAppId | null, appFilters?: any) => void;
}

export const useMiniAppModal = create<MiniAppModalState & MiniAppModalLegacyState>((set) => ({
  activeApp: null,
  activeModal: null, // Pour compatibilité
  filters: {},
  
  setActiveApp: (appId, appFilters) => 
    set(() => {
      // Afficher des informations de débogage
      console.log('setActiveApp appelé avec:', { appId, appFilters });
      
      // Vérifier si les filtres contiennent des IDs de produits mentionnés
      const hasMentionedIds = 
        appId === 'products' && 
        appFilters && 
        ((appFilters.products && appFilters.products.mentionedIds && appFilters.products.mentionedIds.length > 0) ||
         (appFilters.mentionedIds && appFilters.mentionedIds.length > 0));
      
      // Normaliser le format des filtres si nécessaire
      let normalizedFilters = appFilters;
      
      if (hasMentionedIds) {
        // Log pour débogage
        console.log('Filtres de produits mentionnés détectés:', 
          appFilters.products?.mentionedIds || appFilters.mentionedIds);
        
        // Si les IDs sont directement dans appFilters.mentionedIds, normaliser le format
        if (appFilters.mentionedIds && !appFilters.products) {
          normalizedFilters = {
            products: {
              mentionedIds: appFilters.mentionedIds
            }
          };
          console.log('Filtres normalisés:', normalizedFilters);
        }
      }
      
      // Si on ouvre la même app avec de nouveaux filtres, on les remplace
      return {
        activeApp: appId,
        activeModal: appId, // Pour compatibilité
        // Si des filtres sont fournis pour l'app en cours d'ouverture, on les utilise
        // sinon on garde les filtres existants pour cette app
        filters: normalizedFilters && appId ? 
          normalizedFilters as MiniAppFilters : 
          {} as MiniAppFilters
      };
    }),
  
  // Alias pour setActiveApp (pour compatibilité avec MiniAppButton)
  openModal: (appId, appFilters) => 
    set((state) => {
      console.log('openModal appelé avec:', { appId, appFilters });
      return {
        activeApp: appId,
        activeModal: appId, // Pour compatibilité
        // Si des filtres sont fournis, les utiliser, sinon garder les filtres existants
        filters: appFilters ? 
          { [appId]: appFilters } as MiniAppFilters : 
          state.filters
      };
    }),
    
  closeModal: () => set({ activeApp: null, activeModal: null }),
  
  updateFilters: (appId, newFilters) =>
    set((state) => {
      // Récupérer les filtres existants pour cette app ou un objet vide
      let currentAppFilters = {};
      
      // On utilise une approche dynamique pour éviter les erreurs de type
      if (appId in (state.filters || {})) {
        currentAppFilters = (state.filters as any)[appId] || {};
      }
      
      return {
        filters: {
          ...state.filters,
          [appId]: {
            ...currentAppFilters,
            ...newFilters
          }
        } as MiniAppFilters
      };
    }),
}));

// Ajouter un gestionnaire d'événements pour le clic sur le bouton "View Products"
if (typeof window !== 'undefined') {
  // Exposer le store au niveau global pour le rendre accessible aux fonctions inline dans le HTML
  (window as any).useMiniAppStore = {
    setActiveApp: (appId: MiniAppId, filters: any) => {
      console.log('useMiniAppStore.setActiveApp appelé:', appId, filters);
      
      // Pour les produits, adapter le format des filtres
      if (appId === 'products' && filters.mentionedIds) {
        filters = {
          products: {
            mentionedIds: filters.mentionedIds
          }
        };
      }
      
      useMiniAppModal.getState().setActiveApp(appId, filters);
    }
  };

  // Gestionnaire pour l'événement 'open-mini-app'
  window.addEventListener('open-mini-app', (event: any) => {
    const customEvent = event as CustomEvent<{
      appId: MiniAppId;
      filters: any;
    }>;
    
    const { appId, filters } = customEvent.detail;
    console.log('Événement open-mini-app reçu:', appId, filters);
    
    // Utiliser le store Zustand pour ouvrir l'application avec les filtres appropriés
    if (appId && filters) {
      useMiniAppModal.getState().setActiveApp(appId, filters);
    }
  });

  // Gestionnaire pour l'événement 'open-products-miniapp'
  window.addEventListener('open-products-miniapp', (event: any) => {
    const customEvent = event as CustomEvent<{
      productIds: string[];
    }>;
    
    // Extraire les identifiants des produits de l'événement
    let productIds = customEvent.detail?.productIds || [];
    
    // S'assurer que les identifiants sont des chaînes de caractères valides
    productIds = productIds.filter(id => id && typeof id === 'string' && id.trim().length > 0);
    
    console.log('Événement open-products-miniapp reçu avec IDs (après filtrage):', productIds);
    
    // Créer un élément de débogage pour visualiser les identifiants
    const debugElement = document.createElement('div');
    debugElement.textContent = 'Traitement IDs: ' + productIds.join(', ');
    debugElement.style.position = 'fixed';
    debugElement.style.top = '40px';
    debugElement.style.right = '10px';
    debugElement.style.background = 'rgba(0,100,0,0.8)';
    debugElement.style.color = 'white';
    debugElement.style.padding = '10px';
    debugElement.style.borderRadius = '5px';
    debugElement.style.zIndex = '999999';
    debugElement.style.fontSize = '12px';
    document.body.appendChild(debugElement);
    setTimeout(() => debugElement.remove(), 5000);
    
    if (productIds.length > 0) {
      // Créer les filtres pour l'application des produits
      const filters = {
        products: {
          mentionedIds: productIds
        }
      };
      
      // Établir un accès sécurisé au store via getState()
      const store = useMiniAppModal.getState();
      console.log('État du store avant setActiveApp:', store);
      
      // Appeler la méthode setActiveApp du store
      store.setActiveApp('products', filters);
      console.log('setActiveApp appelé avec succès, filtres:', filters);
      
      // Double vérification après 500ms
      setTimeout(() => {
        console.log('État du store après setActiveApp:', useMiniAppModal.getState());
      }, 500);
    }
  });

  // Écouter les messages postMessage
  window.addEventListener('message', (event) => {
    if (event.data && event.data.type === 'OPEN_PRODUCTS' && event.data.productIds) {
      console.log('Message postMessage OPEN_PRODUCTS reçu:', event.data.productIds);
      const filters = {
        products: {
          mentionedIds: event.data.productIds
        }
      };
      useMiniAppModal.getState().setActiveApp('products', filters);
    }
  });

  // Observer les clics sur le bouton des produits
  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    const button = target.closest('.products-button');
    
    if (button) {
      console.log('Clic détecté sur bouton produits');
      const productIds = button.getAttribute('data-product-ids')?.split(',');
      
      if (productIds && productIds.length > 0) {
        const filters = {
          products: {
            mentionedIds: productIds
          }
        };
        
        useMiniAppModal.getState().setActiveApp('products', filters);
      }
    }
  });

  // Observer les mutations DOM pour détecter l'élément spécial
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'childList') {
        const trigger = document.getElementById('open-products-trigger');
        if (trigger) {
          console.log('Élément open-products-trigger détecté dans le DOM');
          try {
            const productIds = JSON.parse(trigger.dataset.productIds || '[]');
            if (productIds.length > 0) {
              const filters = {
                products: {
                  mentionedIds: productIds
                }
              };
              useMiniAppModal.getState().setActiveApp('products', filters);
            }
          } catch (err) {
            console.error('Erreur de parsing productIds:', err);
          }
        }
      }
    }
  });
  
  // Commencer l'observation
  observer.observe(document.body, { childList: true, subtree: true });
}